import { INLINE_CTA_ACTION } from '@storefronts/common-types'
import React, { useContext, useEffect, useRef } from 'react'
import { AnalyticsContext } from '../../../context/analyticsContext'
import { CartContext } from '../../../context/cartContext'
import { CustomizationContext } from '../../../context/customizationContext'
import { PageContext } from '../../../context/PageContextProvider'
import { VariantWithImage } from '../../../types'
import { LockIcon } from '../../icons/Icons'
import { CTAButton } from '../../shared/CTAButton'
import { ShopPayCTAButton } from '../../shared/ShopPayCTAButton'

export const ProductDrawerCTA: React.FC<{
    variant: VariantWithImage
    action?: INLINE_CTA_ACTION
    backgroundColor?: string
    textColor?: string
    loader?: boolean
    border?: string
    /** When true with BUY_NOW, replaces the Buy Now button with Shop Pay. */
    useShopPay?: boolean
    /** Custom label; falls back to the default for the action when omitted. */
    buttonText?: string
    /** Selling plan chosen on the PDP; added to the cart line as a subscription. */
    sellingPlanId?: string | null
}> = ({
    variant,
    action = INLINE_CTA_ACTION.ADD_TO_CART,
    backgroundColor,
    textColor,
    loader = false,
    border,
    useShopPay = false,
    buttonText,
    sellingPlanId = null,
}) => {
    const { Analytics } = useContext(AnalyticsContext)
    const { setIsCartOpen, setActiveLPLink, registerCartActionButton } =
        useContext(PageContext)
    const { addCartItem } = useContext(CartContext)
    const { primaryCtaTextColor } = useContext(CustomizationContext)
    const buttonRef = useRef<HTMLDivElement | null>(null)
    const cleanupRef = useRef<(() => void) | null>(null)

    useEffect(() => {
        if (!buttonRef.current) return undefined
        cleanupRef.current = registerCartActionButton(buttonRef.current)
        return () => {
            if (cleanupRef.current) {
                cleanupRef.current()
                cleanupRef.current = null
            }
        }
    }, [registerCartActionButton])

    const _onAddToCartClick = () => {
        Analytics.TRACK_LANDING_PAGE_CTA_CLICK({
            type: 'email_vlp_primary',
            location: 'pdp',
        })

        addCartItem(variant, 1, sellingPlanId)
        setIsCartOpen(true)
        setActiveLPLink(null)
    }

    const _onBuyNowClick = () => {
        Analytics.TRACK_LANDING_PAGE_CTA_CLICK({
            type: 'email_vlp_buy_now',
            location: 'pdp',
        })
        Analytics.TRACK_CART_CHECKOUT_CLICK({ buyNowVariant: variant })

        addCartItem(variant, 1, sellingPlanId, true)
        setActiveLPLink(null)
    }

    const _onShopPayClick = () => {
        Analytics.TRACK_LANDING_PAGE_CTA_CLICK({
            type: 'email_vlp_buy_now',
            location: 'pdp',
        })
        Analytics.TRACK_CART_CHECKOUT_CLICK({ buyNowVariant: variant })

        addCartItem(variant, 1, sellingPlanId, true, true)
        setActiveLPLink(null)
    }

    const wrapperStyle: React.CSSProperties = {
        width: '100%',
        flex: '1 1 0',
        minWidth: 0,
    }

    const buyNowButtonText = (
        <span style={{ display: 'flex', alignItems: 'center' }}>
            <LockIcon color={textColor || primaryCtaTextColor} />
            {buttonText?.trim() || 'Buy Now'}
        </span>
    )

    if (action === INLINE_CTA_ACTION.BUY_NOW) {
        if (useShopPay) {
            return (
                <div ref={buttonRef} style={wrapperStyle}>
                    <ShopPayCTAButton
                        onClick={_onShopPayClick}
                        disabled={!variant.available}
                        loader={loader}
                        fullWidth
                    />
                </div>
            )
        }
        return (
            <div ref={buttonRef} style={wrapperStyle}>
                <CTAButton
                    buttonText={buyNowButtonText}
                    fitLabel={buttonText?.trim() || 'Buy Now'}
                    onClick={_onBuyNowClick}
                    loader={loader}
                    height={48}
                    disabled={!variant.available}
                    backgroundColor={backgroundColor}
                    textColor={textColor}
                    border={border}
                />
            </div>
        )
    }

    return (
        <div ref={buttonRef} style={wrapperStyle}>
            <CTAButton
                buttonText={buttonText?.trim() || 'Add to Cart'}
                onClick={_onAddToCartClick}
                loader={loader}
                height={48}
                disabled={!variant.available}
                backgroundColor={backgroundColor}
                textColor={textColor}
                border={border}
            />
        </div>
    )
}
